import { ANNOTATION_RESOLVED_OPACITY } from './annotation-labels'
import { filterPlottableAnnotations } from './annotation-utils'

/**
 * A lng/lat rectangle, in degrees. Never crosses the antimeridian: every metro
 * the layer is drawn for is far from it, so `west < east` always holds.
 */
export type AnnotationGeoBox = {
  west: number
  south: number
  east: number
  north: number
}

type Located = { location: { latitude: number; longitude: number } }

/**
 * The zoom from which annotations are drawn as full markers. Below it every
 * note is a plain circle in its severity colour, so a zoomed-out map shows the
 * spread at a glance and costs nothing to pan.
 *
 * 11 is about a whole city across, two steps out from where the route builder
 * opens (13), so the route builder opens on markers unless the area is a
 * hotspot (see {@link ANNOTATION_MARKER_BUDGET}), and only zooming out past
 * the city turns them into circles.
 */
export const ANNOTATION_MARKER_MIN_ZOOM = 11

/**
 * Most notes drawn as full markers at once, even past
 * {@link ANNOTATION_MARKER_MIN_ZOOM}.
 *
 * Each marker is a DOM node MapLibre repositions on every camera frame, plus a
 * popup and a portaled React tree, so several hundred make panning stutter. A
 * view with more notes than this (a hotspot) stays circles until the user
 * zooms in further. It is all or nothing, never a mix of the two.
 */
export const ANNOTATION_MARKER_BUDGET = 300

/**
 * How far past each edge of the viewport markers are still built, as a share
 * of its width and height. A short pan then lands on markers that already
 * exist, rather than on dots that turn into markers once the camera stops.
 */
export const ANNOTATION_VIEWPORT_PADDING = 0.25

/**
 * Half the side of the square a metro's annotations are drawn from: 50 miles,
 * the same box trip-manager's `MetroBounds` filters `metropolisId` with.
 */
export const ANNOTATION_METRO_RADIUS_M = 80_467

const METERS_PER_DEGREE_LATITUDE = 111_320

/** Grows `box` by `fraction` of its own width and height on each side. */
export const padAnnotationBox = (
  box: AnnotationGeoBox,
  fraction: number,
): AnnotationGeoBox => {
  const dLng = (box.east - box.west) * fraction
  const dLat = (box.north - box.south) * fraction
  return {
    west: Math.max(-180, box.west - dLng),
    south: Math.max(-90, box.south - dLat),
    east: Math.min(180, box.east + dLng),
    north: Math.min(90, box.north + dLat),
  }
}

/** Whether `inner` lies entirely inside `outer`. */
export const annotationBoxContains = (
  outer: AnnotationGeoBox,
  inner: AnnotationGeoBox,
): boolean =>
  inner.west >= outer.west &&
  inner.south >= outer.south &&
  inner.east <= outer.east &&
  inner.north <= outer.north

/**
 * How far the zoom has to move before the split is worked out again, even
 * though the camera is still inside the padded box.
 */
export const ANNOTATION_VIEWPORT_ZOOM_STEP = 0.5

/** The padded box the marker split was last worked out for, and its zoom. */
export type AnnotationViewport = {
  box: AnnotationGeoBox
  zoom: number
}

/** Whether `zoom` is close enough for full markers. */
export const showsAnnotationMarkers = (zoom: number): boolean =>
  zoom >= ANNOTATION_MARKER_MIN_ZOOM

/**
 * The viewport the layer should split against after the camera stopped at
 * `visible` and `zoom`.
 *
 * Returns `current` itself while the camera stays inside its padded box, near
 * its zoom and on the same side of {@link ANNOTATION_MARKER_MIN_ZOOM}, so a
 * pan or zoom that stays inside re-renders nothing. Cheap enough to run on
 * every frame of a camera move. Otherwise it returns a new box, `visible`
 * padded by {@link ANNOTATION_VIEWPORT_PADDING}. Crossing the marker zoom
 * always re-measures, however small the step, or a zoom from 13.8 to 14.1
 * would leave the circles in place.
 */
export const nextAnnotationViewport = (
  current: AnnotationViewport | null,
  visible: AnnotationGeoBox,
  zoom: number,
): AnnotationViewport =>
  current &&
  Math.abs(zoom - current.zoom) < ANNOTATION_VIEWPORT_ZOOM_STEP &&
  showsAnnotationMarkers(zoom) === showsAnnotationMarkers(current.zoom) &&
  annotationBoxContains(current.box, visible)
    ? current
    : { box: padAnnotationBox(visible, ANNOTATION_VIEWPORT_PADDING), zoom }

export const isInAnnotationBox = (
  { location: { latitude, longitude } }: Located,
  box: AnnotationGeoBox,
): boolean =>
  latitude >= box.south &&
  latitude <= box.north &&
  longitude >= box.west &&
  longitude <= box.east

/**
 * The square around a metro's origin that its annotations are drawn from.
 *
 * Mirrors trip-manager's `MetroBounds.boxFor`, so a backend that ignores
 * `metropolisId` (the triage filters switched off) is still narrowed to the
 * same area, and a backend that honours it gets the same answer twice.
 */
export const annotationMetroBox = (
  origin: { latitude: number; longitude: number },
  radiusMeters = ANNOTATION_METRO_RADIUS_M,
): AnnotationGeoBox => {
  const dLat = radiusMeters / METERS_PER_DEGREE_LATITUDE
  const dLng = dLat / Math.cos((origin.latitude * Math.PI) / 180)
  return {
    west: origin.longitude - dLng,
    south: origin.latitude - dLat,
    east: origin.longitude + dLng,
    north: origin.latitude + dLat,
  }
}

/** Keeps the annotations inside `box`. */
export const filterAnnotationsWithinBox = <T extends Located>(
  annotations: T[],
  box: AnnotationGeoBox,
): T[] => annotations.filter(annotation => isInAnnotationBox(annotation, box))

export type AnnotationRenderSplit<T> = {
  /** Drawn as full DOM markers with hover popups. */
  markers: T[]
  /** Drawn as GL dots. */
  dots: T[]
}

/**
 * Decides which annotations are full DOM markers and which are circles.
 *
 * - Below {@link ANNOTATION_MARKER_MIN_ZOOM}, every note is a circle.
 * - From it, every note inside `viewport` is a marker, and the ones outside
 *   are circles (off screen, so they cost nothing and are already in place
 *   when a pan brings them into view).
 * - If more than `budget` notes are in view, every note stays a circle.
 *
 * So at any moment the notes on screen are all one or all the other.
 *
 * A `null` viewport (the camera not measured yet) draws circles only.
 * Unplottable rows are dropped here, as they are everywhere else on the layer.
 */
export const splitAnnotationsForViewport = <T extends Located>(
  annotations: T[],
  viewport: AnnotationViewport | null,
  budget = ANNOTATION_MARKER_BUDGET,
): AnnotationRenderSplit<T> => {
  const plottable = filterPlottableAnnotations(annotations)
  const allCircles = { markers: [], dots: plottable }
  if (!viewport || !showsAnnotationMarkers(viewport.zoom)) return allCircles
  const inView: T[] = []
  const outOfView: T[] = []
  for (const annotation of plottable) {
    if (isInAnnotationBox(annotation, viewport.box)) inView.push(annotation)
    else outOfView.push(annotation)
  }
  if (inView.length > budget) return allCircles
  return { markers: inView, dots: outOfView }
}

export type AnnotationDotProperties = {
  color: string
  opacity: number
}

/**
 * GeoJSON for the dot layer. Each dot takes the colour its marker would have,
 * and a resolved note is faded the way its marker is.
 */
export const buildAnnotationDotFeatures = <
  T extends Located & { resolution: unknown },
>(
  annotations: T[],
  color: (annotation: T) => string,
): GeoJSON.FeatureCollection<GeoJSON.Point, AnnotationDotProperties> => ({
  type: 'FeatureCollection',
  features: annotations.map(annotation => ({
    type: 'Feature',
    properties: {
      color: color(annotation),
      opacity: annotation.resolution ? ANNOTATION_RESOLVED_OPACITY : 1,
    },
    geometry: {
      type: 'Point',
      coordinates: [
        annotation.location.longitude,
        annotation.location.latitude,
      ],
    },
  })),
})
